import type { RouteComponentProps } from '@reach/router';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TableRecordDeletion, TableTaskStatus } from '@components/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import TaskModalContent from '@components/Modal/Content/TaskModalContent';
import NullStateDiv from '@components/NullStateDiv';

import { isDevelopment } from '@constants/env';

import { useDeleteCuratorTask } from '@hooks/api/curator-task';
import { useModal } from '@hooks/useModal';

import { TableContainer } from '@layouts/Layouts/TableLayout';

import { TaskPriorityMap } from '@utils/curator-task';
import { formatDate, formatName } from '@utils/format';

import type { GetAllCuratorTaskRecordsQuery } from 'generated/curator-task.generated';
import type { CuratorTaskModel, TaskOwnership } from 'generated/types';

const messages = defineMessages({
  noTasks: {
    id: 'tasksMain.noTasks',
    defaultMessage: 'NO TASKS',
  },
});
type ITask = ArrayElement<
  GetAllCuratorTaskRecordsQuery['getAllCuratorTaskRecords']['data']
>;

const MasterTaskList: React.FC<
  RouteComponentProps & {
    tasks?: ITask[] | null;
    ownership: TaskOwnership;
  }
> = ({ tasks = [], ownership }) => {
  const { formatMessage } = useIntl();
  const { closeModal, openModal, setContent } = useModal();
  const [deleteCuratorTask, deleteCuratorTaskLoading] = useDeleteCuratorTask();

  const tableData = (tasks ?? []).map((task) => {
    const {
      created_at,
      curator_task_id,
      description,
      due_date,
      name,
      priority,
      status,
      user,
      vitall_patient,
    } = task;
    const patient = vitall_patient?.user;
    const clientName = `${
      patient ? formatName(patient.first_name, patient.last_name) : '-'
    }`;
    const assigneeName = `${
      user ? formatName(user.first_name, user.last_name) : '-'
    }`;

    return {
      curator_task_id,
      dateCreated: formatDate({ date: created_at }),
      name,
      client: clientName,
      description,
      due_date,
      formattedDueDate: formatDate({ date: due_date }),
      priority,
      status: Boolean(status),
      user: assigneeName,
      vitall_patient_id: vitall_patient?.vitall_patient_id,
      task,
    };
  });

  const handleOpenModal = (task?: ITask) => {
    openModal({
      width: 400,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: <TaskModalContent onSubmit={closeModal} task={task} />,
    });
  };

  return (
    <TableContainer>
      {tableData.length ? (
        <MuiDataTable
          columns={[
            {
              name: 'task',
              label: 'Status',
              options: {
                customBodyRender: (task: CuratorTaskModel) => (
                  <TableTaskStatus task={task} />
                ),
              },
            },
            {
              name: 'dateCreated',
              className: 'date',
              label: 'Date Submitted',
              options: { filter: true, sort: true },
              desktopOnly: true,
            },
            {
              name: 'name',
              className: 'small',
              label: 'Task Name',
              options: { filter: true, searchable: true, sort: true },
            },
            {
              name: 'client',
              className: 'blue-hover',
              label: 'Client',
              options: { filter: true, searchable: true, sort: true },
            },
            {
              name: 'formattedDueDate',
              className: 'date',
              label: 'Due Date',
              options: { filter: true, sort: true },
            },
            {
              name: 'description',
              className: 'small',
              label: 'Description',
              options: { filter: true, searchable: true, sort: true },
            },
            {
              name: 'priority',
              className: 'small',
              label: 'Priority',
              options: {
                filter: true,
                customBodyRender: (value: number) =>
                  TaskPriorityMap[value].text,
              },
            },
            {
              name: 'user',
              className: 'blue-hover',
              label: 'Assigned To',
              options: {
                filter: true,
                searchable: true,
                sort: true,
                display: ownership === 'ALL',
              },
            },
            {
              name: 'curator_task_id',
              options: {
                customHeadLabelRender: () => null,
                customBodyRender: (id: string) => {
                  if (!id) return;
                  return (
                    <TableRecordDeletion
                      loading={deleteCuratorTaskLoading}
                      onDelete={async () => {
                        try {
                          await deleteCuratorTask({
                            input: { curator_task_id: id },
                          });
                        } catch (e) {
                          if (isDevelopment) {
                            // eslint-disable-next-line no-console
                            console.error(e);
                          }
                        }
                      }}
                    />
                  );
                },
              },
            },
          ]}
          data={tableData}
          options={{
            onRowClick: (_rowData, { dataIndex }) => {
              if (dataIndex <= tableData.length - 1) {
                const row = tableData[dataIndex];
                handleOpenModal(row.task);
              }
            },
            sortOrder: {
              name: 'due_date',
              direction: 'asc',
            },
            sort: true,
            search: true,
            filter: true,
          }}
        />
      ) : (
        <NullStateDiv>{formatMessage(messages.noTasks)}</NullStateDiv>
      )}
    </TableContainer>
  );
};

export default MasterTaskList;
